import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HomeIcon, PlusCircleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import ListaNarzedzi from './ListaNarzedzi'
import ToggleThemeButton from '../ToggleThemeButton'

/**
 * Lista narzędzi wysuwana z lewej na telefonie i tablecie (poniżej `lg`,
 * gdzie stały pasek się nie mieści). Otwiera ją przycisk menu w górnym pasku.
 *
 * Zamyka się: kliknięciem w tło, klawiszem Escape, przyciskiem X i wyborem
 * pozycji. Zmianę strony obsługuje dodatkowo `Navbar`, bo kliknięcie w link do
 * strony, na której już się jest, nie zmienia adresu.
 *
 * Portal do `body`: górny pasek ma `sticky z-20`, czyli własny kontekst warstw,
 * i szuflada narysowana w nim leżałaby POD porównywarką i kafelkiem błędów
 * (`z-40`).
 *
 * Zawsze w drzewie, schowana przez `invisible`: dzięki temu wysuwa się
 * płynnie, a zamknięta nie łapie fokusu ani nie istnieje dla czytnika.
 */

// `visibility` przełącza się OD RAZU przy otwieraniu (inaczej przez pierwszą
// klatkę szuflada jest jeszcze ukryta i przycisk X nie przyjmuje fokusu),
// a przy zamykaniu dopiero po wysunięciu, żeby animacja była widoczna.
const PRZEJSCIE_OTWARTE = '[transition:transform_200ms_ease,opacity_200ms_ease,visibility_0s]'
const PRZEJSCIE_ZAMKNIETE = '[transition:transform_200ms_ease,opacity_200ms_ease,visibility_0s_linear_200ms]'

// `dlaFirm` (2026-10-24): „Dla firm PV” na telefonie. W pasku przycisk jest dopiero od `md`, więc poniżej firma nie
// miała jak trafić na `/dla-firm` (pierwszy krok drogi firmy). Te same warunki co w pasku liczy `Navbar`.
const SzufladaNarzedzi = ({ id, otwarta, onZamknij, przyciskRef, dlaFirm = false, stronaGlowna = false }) => {
  const { t } = useTranslation('wspolne')
  const panel = useRef(null)
  const zamknij = useRef(null)

  useEffect(() => {
    if (!otwarta) return undefined

    // Strona pod spodem nie przewija się razem z listą.
    const poprzedni = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    zamknij.current?.focus()

    const klawisz = (e) => {
      if (e.key === 'Escape') { onZamknij(); return }
      if (e.key !== 'Tab' || !panel.current) return
      // Tab krąży w obrębie szuflady: fokus za nią trafiałby w zasłoniętą stronę.
      const elementy = panel.current.querySelectorAll('a[href], button:not([disabled])')
      if (!elementy.length) return
      const pierwszy = elementy[0]
      const ostatni = elementy[elementy.length - 1]
      if (e.shiftKey && document.activeElement === pierwszy) { e.preventDefault(); ostatni.focus() }
      else if (!e.shiftKey && document.activeElement === ostatni) { e.preventDefault(); pierwszy.focus() }
    }
    document.addEventListener('keydown', klawisz)

    // Okno poszerzone do `lg` przy otwartej szufladzie: pojawia się stały pasek,
    // a szuflada (`lg:hidden`) znika, ale blokada przewijania by została.
    const szeroko = typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 1024px)') : null
    const naSzeroko = () => { if (szeroko.matches) onZamknij() }
    szeroko?.addEventListener('change', naSzeroko)

    const przycisk = przyciskRef?.current
    return () => {
      document.body.style.overflow = poprzedni
      document.removeEventListener('keydown', klawisz)
      szeroko?.removeEventListener('change', naSzeroko)
      // Fokus wraca tam, skąd szufladę otwarto, a nie na początek strony.
      przycisk?.focus()
    }
  }, [otwarta, onZamknij, przyciskRef])

  return createPortal(
    <div className='lg:hidden print:hidden'>
      <div
        aria-hidden='true'
        onClick={onZamknij}
        className={`fixed inset-0 z-50 bg-gray-900/40 motion-reduce:transition-none
                    ${otwarta ? `visible opacity-100 ${PRZEJSCIE_OTWARTE}` : `invisible opacity-0 ${PRZEJSCIE_ZAMKNIETE}`}`}
      />
      <div
        id={id}
        ref={panel}
        role='dialog'
        aria-modal='true'
        aria-label={t('nav.menu')}
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col bg-white shadow-xl
                    motion-reduce:transition-none
                    ${otwarta ? `visible translate-x-0 ${PRZEJSCIE_OTWARTE}` : `invisible -translate-x-full ${PRZEJSCIE_ZAMKNIETE}`}`}
      >
        <div className='h-16 flex-shrink-0 flex items-center justify-between gap-3 px-4 border-b border-gray-100'>
          <Link to='/' onClick={onZamknij} className='flex-shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'>
            <img src={`${process.env.PUBLIC_URL}/navLogo.png`} alt={t('narzedzia.logo_alt')} className='w-24 h-auto' />
          </Link>
          <span className='flex items-center gap-1'>
            {/* Na telefonie przełącznik motywu mieszka tutaj: w pasku jego miejsce zajmuje „Ogłoś na Rynku” (2026-10-23). */}
            <span className='sm:hidden'>
              <ToggleThemeButton />
            </span>
            <button
              ref={zamknij}
              type='button'
              onClick={onZamknij}
              aria-label={t('narzedzia.zamknij')}
              className='p-2 -mr-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
            >
              <XMarkIcon aria-hidden='true' className='w-6 h-6' />
            </button>
          </span>
        </div>
        <nav aria-label={t('narzedzia.przycisk')} className='flex-1 overflow-y-auto px-3 py-4'>
          {dlaFirm && (
            <Link to='/dla-firm' onClick={onZamknij}
                  className='md:hidden mb-4 flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white no-underline hover:bg-blue-700'>
              <PlusCircleIcon aria-hidden='true' className='w-5 h-5' />{t('nav.dla_firm_pv')}
            </Link>
          )}
          {stronaGlowna && (
            <Link to='/' onClick={onZamknij}
                  className='mb-4 flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white no-underline hover:bg-blue-500'>
              <HomeIcon aria-hidden='true' className='w-5 h-5' />{t('nav.strona_glowna')}
            </Link>
          )}
          <ListaNarzedzi idPrefix='szuflada' onWybor={onZamknij} />
        </nav>
      </div>
    </div>,
    document.body,
  )
}

export default SzufladaNarzedzi
